import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Inbox, Radio, Copy, CalendarPlus, TrendingUp, RefreshCw, Users, Target } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGES = [7, 30, 90];

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—";

const Kpi = ({ label, value, testid }) => (
  <div className="border border-slate-800 bg-slate-900/50 p-5" data-testid={testid}>
    <p className="font-serif text-3xl text-white leading-none">{value}</p>
    <p className="text-[10px] uppercase tracking-widest text-slate-500 mt-3">{label}</p>
  </div>
);

const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("bbc_admin_token")}` } });

const CalendarFeedBanner = () => {
  const [feed, setFeed] = useState(null);
  const [rotating, setRotating] = useState(false);
  useEffect(() => {
    axios
      .get(`${API}/admin/calendar-feed`, authHeader())
      .then((res) => setFeed(res.data))
      .catch(() => setFeed(null));
  }, []);
  if (!feed) return null;
  const copy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(feed.url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = feed.url;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("iCal feed URL copied");
    } catch {
      toast.error("Could not copy — select and copy the URL manually.");
    }
  };
  const rotate = async () => {
    if (!window.confirm("Rotating will immediately break the current calendar subscription URL. You'll need to update Google/Apple Calendar with the new URL. Continue?")) return;
    setRotating(true);
    try {
      const res = await axios.post(`${API}/admin/calendar-feed/rotate`, {}, authHeader());
      setFeed(res.data);
      toast.success("New URL generated — old link is now invalid");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not rotate the token");
    } finally {
      setRotating(false);
    }
  };
  return (
    <div
      className="border border-[#D4AF37]/40 bg-[#D4AF37]/[0.05] p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      data-testid="admin-ical-feed"
    >
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] mb-1 flex items-center gap-2">
          <CalendarPlus size={13} /> Subscribable calendar
        </p>
        <p className="text-white text-sm">
          Paste the URL below into Google Calendar → Add other calendars → From URL, or Apple Calendar → File → New
          Calendar Subscription. Bookings will appear automatically.
        </p>
        <code className="block mt-3 text-slate-400 text-xs font-mono truncate max-w-2xl">{feed.url}</code>
      </div>
      <div className="flex flex-wrap gap-2 shrink-0">
        <button
          onClick={copy}
          data-testid="ical-copy-btn"
          className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-4 py-2 text-xs uppercase tracking-widest hover:bg-[#D4AF37] hover:text-slate-900 transition-colors"
        >
          <Copy size={13} /> Copy URL
        </button>
        <button
          onClick={rotate}
          disabled={rotating}
          data-testid="ical-rotate-btn"
          className="inline-flex items-center gap-2 border border-slate-700 text-slate-300 px-4 py-2 text-xs uppercase tracking-widest hover:border-red-500 hover:text-red-400 disabled:opacity-50 transition-colors"
        >
          {rotating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw size={13} />}
          Rotate URL
        </button>
      </div>
    </div>
  );
};

const AttributionPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios
      .get(`${API}/admin/attribution`, authHeader())
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);
  if (loading) {
    return (
      <div className="flex items-center justify-center py-10" data-testid="attribution-loading">
        <Loader2 className="animate-spin text-[#D4AF37]" size={20} />
      </div>
    );
  }
  if (!data) return null;
  const maxRevenue = Math.max(1, ...data.by_source.map((r) => r.revenue_eur));
  return (
    <div className="space-y-6" data-testid="attribution-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-xl text-white flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#D4AF37]" /> Attribution — where paid bookings come from
        </h3>
        <div className="text-right">
          <p className="font-serif text-2xl text-[#D4AF37] leading-none">€{Number(data.totals.revenue_eur).toLocaleString()}</p>
          <p className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">
            {data.totals.paid_bookings} paid all-time
          </p>
        </div>
      </div>

      <div className="border border-slate-800 bg-slate-900/40 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-950/60">
              <th className="text-left py-3 px-4 text-[10px] uppercase tracking-widest text-slate-500">Source</th>
              <th className="text-left py-3 px-4 text-[10px] uppercase tracking-widest text-slate-500">Campaign</th>
              <th className="text-right py-3 px-4 text-[10px] uppercase tracking-widest text-slate-500">Bookings</th>
              <th className="text-right py-3 px-4 text-[10px] uppercase tracking-widest text-slate-500">Paid</th>
              <th className="text-right py-3 px-4 text-[10px] uppercase tracking-widest text-slate-500">Convert</th>
              <th className="text-right py-3 px-4 text-[10px] uppercase tracking-widest text-slate-500">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {data.by_source.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-slate-500 text-sm">
                  No booking attribution data yet.
                </td>
              </tr>
            )}
            {data.by_source.map((row, idx) => {
              const bar = (row.revenue_eur / maxRevenue) * 100;
              return (
                <tr
                  key={`${row.utm_source}-${row.utm_campaign}-${idx}`}
                  className="border-b border-slate-800/60 hover:bg-slate-900/40"
                  data-testid={`attribution-row-${idx}`}
                >
                  <td className="py-3 px-4 text-white text-sm">{row.utm_source}</td>
                  <td className="py-3 px-4 text-slate-400 text-xs font-mono">{row.utm_campaign}</td>
                  <td className="py-3 px-4 text-right text-slate-300 text-sm">{row.bookings}</td>
                  <td className="py-3 px-4 text-right text-white text-sm">{row.paid}</td>
                  <td className="py-3 px-4 text-right text-slate-300 text-sm">{row.conversion}%</td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="h-1 w-16 bg-slate-800 relative">
                        <div className="absolute inset-y-0 left-0 bg-[#D4AF37]" style={{ width: `${bar}%` }} />
                      </div>
                      <span className="text-[#D4AF37] text-sm font-mono w-16 text-right">
                        €{Number(row.revenue_eur).toLocaleString()}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-slate-800 bg-slate-900/40 p-5">
          <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-4">By hero variant (A/B)</p>
          {data.by_variant.length === 0 ? (
            <p className="text-slate-500 text-xs">No variant traffic yet.</p>
          ) : (
            <div className="space-y-3">
              {data.by_variant.map((row) => (
                <div key={row.variant} className="flex items-center justify-between text-sm" data-testid={`variant-${row.variant}`}>
                  <span className="text-slate-200">{row.variant}</span>
                  <span className="text-slate-400 text-xs">
                    {row.paid}/{row.bookings} · {row.conversion}%
                  </span>
                  <span className="text-[#D4AF37] font-mono w-20 text-right">€{Number(row.revenue_eur).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="border border-slate-800 bg-slate-900/40 p-5">
          <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-4">Paid bookings by weekday</p>
          {data.by_day_of_week.length === 0 ? (
            <p className="text-slate-500 text-xs">Once you have more paid bookings this chart will fill in.</p>
          ) : (
            <div className="space-y-2">
              {data.by_day_of_week.map((row) => {
                const dayMax = Math.max(1, ...data.by_day_of_week.map((r) => r.paid));
                const pct = (row.paid / dayMax) * 100;
                return (
                  <div key={row.day} className="flex items-center gap-3" data-testid={`dow-${row.day}`}>
                    <span className="text-slate-400 text-xs w-8">{row.day}</span>
                    <div className="flex-1 h-2 bg-slate-800">
                      <div className="h-full bg-[#D4AF37]" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-slate-300 text-xs w-14 text-right">
                      {row.paid} · €{Number(row.revenue_eur).toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DigestSettingsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recipientsText, setRecipientsText] = useState("");
  const [goal, setGoal] = useState(1000);
  const [cancelFee, setCancelFee] = useState(25);
  const [streakCount, setStreakCount] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/admin/digest-settings`, authHeader())
      .then((res) => {
        setRecipientsText((res.data.recipients || []).join("\n"));
        setGoal(res.data.revenue_goal_eur || 1000);
        setCancelFee(res.data.cancel_fee_eur ?? 25);
        setStreakCount(res.data.streak_count || 0);
      })
      .catch(() => null)
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const recipients = recipientsText
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (recipients.length > 5) {
        toast.error("Maximum 5 CC recipients — trim the list first");
        setSaving(false);
        return;
      }
      const res = await axios.post(
        `${API}/admin/digest-settings`,
        {
          recipients,
          revenue_goal_eur: Number(goal) || 0,
          cancel_fee_eur: Number(cancelFee) || 0,
        },
        authHeader(),
      );
      setRecipientsText((res.data.recipients || []).join("\n"));
      setGoal(res.data.revenue_goal_eur);
      setCancelFee(res.data.cancel_fee_eur ?? 25);
      toast.success("Digest settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;
  return (
    <div className="border border-slate-800 bg-slate-900/50 p-6 md:p-8" data-testid="digest-settings-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h3 className="font-serif text-lg text-white flex items-center gap-2">
          <Users className="w-4 h-4 text-[#D4AF37]" />
          Weekly digest — recipients, goal & cancellation fee
        </h3>
        {streakCount >= 1 && (
          <span
            data-testid="digest-streak-badge"
            className={`inline-flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-[0.24em] ${
              streakCount >= 2 ? "bg-green-500/20 text-green-400 border border-green-500/40" : "bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40"
            }`}
          >
            {streakCount >= 2 ? "🔥" : "★"} {streakCount} week{streakCount === 1 ? "" : "s"} in a row
          </span>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-2">
            Extra CC recipients (max 5, one per line)
          </label>
          <textarea
            data-testid="digest-recipients-input"
            value={recipientsText}
            onChange={(e) => setRecipientsText(e.target.value)}
            rows={5}
            placeholder="cofounder@yourdomain.com&#10;accountant@yourdomain.com"
            className="w-full bg-slate-950 border border-slate-800 focus:border-[#D4AF37]/60 outline-none text-slate-200 text-sm font-mono p-3"
          />
          <p className="text-slate-500 text-xs mt-2">
            Admin ({window.__ADMIN_EMAIL__ || "jon.gruda@badbossclinic.com"}) always receives the digest — these
            recipients are cc'd.
          </p>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-2 flex items-center gap-2">
              <Target size={11} /> Weekly revenue goal (EUR)
            </label>
            <input
              type="number"
              min="0"
              step="50"
              data-testid="digest-goal-input"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 focus:border-[#D4AF37]/60 outline-none text-slate-200 text-lg font-mono p-3"
            />
            <p className="text-slate-500 text-xs mt-2">
              Progress bar + streak badge on every Monday digest.
            </p>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-2">
              Cancellation fee (EUR)
            </label>
            <input
              type="number"
              min="0"
              step="5"
              data-testid="digest-cancel-fee-input"
              value={cancelFee}
              onChange={(e) => setCancelFee(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 focus:border-[#D4AF37]/60 outline-none text-slate-200 text-lg font-mono p-3"
            />
            <p className="text-slate-500 text-xs mt-2">
              Only applies to cancellations inside the 24h window. Cancellations booked more than 24 hours in advance
              are refunded in full.
            </p>
          </div>
        </div>
      </div>
      <div className="flex justify-end mt-6">
        <button
          onClick={save}
          disabled={saving}
          data-testid="digest-settings-save"
          className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-5 py-2 text-xs uppercase tracking-widest hover:bg-[#D4AF37] hover:text-slate-900 disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
        </button>
      </div>
    </div>
  );
};

export const AdminMarketing = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [digestSending, setDigestSending] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const saveSpend = async (row, value) => {
    const spend = parseFloat(value);
    if (Number.isNaN(spend) || spend === (row.spend_eur || 0)) return;
    try {
      await axios.post(
        `${API}/admin/marketing/spend`,
        { source: row.source, medium: row.medium, campaign: row.campaign, spend_eur: spend },
        authHeader()
      );
      toast.success("Spend saved");
      setRefreshTick((t) => t + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save spend");
    }
  };

  const sendDigest = async () => {
    setDigestSending(true);
    try {
      const res = await axios.post(`${API}/admin/digest/run`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem("bbc_admin_token")}` },
      });
      const lw = res.data.digest || {};
      toast.success(
        `Digest emailed: €${(lw.revenue_eur || 0).toLocaleString()} revenue, ${lw.paid_bookings || 0} paid bookings last week`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the digest");
    } finally {
      setDigestSending(false);
    }
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios
      .get(`${API}/admin/marketing?days=${days}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("bbc_admin_token")}` },
      })
      .then((res) => active && setData(res.data))
      .catch(() => active && setData(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [days, refreshTick]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="marketing-loading">
        <Loader2 className="animate-spin text-[#D4AF37]" size={22} />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="border border-dashed border-slate-800 p-12 text-center" data-testid="marketing-error">
        <Inbox className="text-slate-700 mx-auto mb-4" size={24} />
        <p className="text-slate-500 text-sm">Could not load marketing data.</p>
      </div>
    );
  }

  const step = (event) => data.steps.find((s) => s.event === event) || { count: 0, people: 0 };
  const pct = (value) => (data.visitors ? `${Math.round((value / data.visitors) * 100)}%` : "0%");
  const maxCount = Math.max(1, ...data.steps.map((s) => s.count));

  return (
    <div className="space-y-8" data-testid="admin-marketing">
      <CalendarFeedBanner />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2" data-testid="marketing-range">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setDays(r)}
              data-testid={`marketing-range-${r}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                days === r
                  ? "border-[#D4AF37] text-[#D4AF37]"
                  : "border-slate-800 text-slate-500 hover:border-slate-700 hover:text-slate-300"
              }`}
            >
              {r} days
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={sendDigest}
            disabled={digestSending}
            data-testid="digest-send-now-btn"
            className="inline-flex items-center gap-2 border border-slate-700 text-slate-300 px-4 py-2 text-xs uppercase tracking-widest hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-50 transition-colors"
          >
            {digestSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Inbox className="w-3 h-3" />}
            Send digest now
          </button>
          <p className="text-slate-500 text-xs flex items-center gap-2" data-testid="marketing-posthog-status">
            <Radio size={13} className={data.posthog_forwarding ? "text-[#D4AF37]" : "text-slate-700"} />
            {data.posthog_forwarding
              ? "Mirroring every event to PostHog"
              : "First-party only — add POSTHOG_API_KEY to mirror to PostHog"}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi label="Visitors" value={data.visitors} testid="marketing-kpi-visitors" />
        <Kpi label="Quiz completions" value={step("quiz_completed").count} testid="marketing-kpi-quiz" />
        <Kpi label="Portal signups" value={step("signup_completed").count} testid="marketing-kpi-signups" />
        <Kpi label="Checkouts started" value={step("booking_checkout_started").count + step("subscription_checkout_started").count} testid="marketing-kpi-checkouts" />
        <Kpi label="Paid conversions" value={step("booking_paid").count + step("subscription_activated").count} testid="marketing-kpi-paid" />
        <Kpi label="Revenue" value={`€${(data.revenue_eur || 0).toLocaleString()}`} testid="marketing-kpi-revenue" />
      </div>

      <div className="border border-slate-800 bg-slate-900/50 p-6" data-testid="marketing-funnel">
        <h3 className="font-serif text-lg text-white mb-6">Funnel · last {data.days} days</h3>
        <div className="space-y-4">
          {data.steps.map((s) => (
            <div key={s.event} className="flex items-center gap-4" data-testid={`funnel-step-${s.event}`}>
              <span className="text-slate-400 text-xs w-56 shrink-0">{s.label}</span>
              <div className="flex-1 h-[6px] bg-slate-800">
                <div className="h-full bg-[#D4AF37]" style={{ width: `${(s.count / maxCount) * 100}%` }} />
              </div>
              <span className="text-white font-mono text-xs w-16 text-right">{s.count}</span>
              <span className="text-slate-500 font-mono text-xs w-20 text-right">{s.people} people</span>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-slate-800 bg-slate-900/50" data-testid="marketing-campaigns">
        <div className="p-6 pb-4">
          <h3 className="font-serif text-lg text-white">Campaign ROI · spend vs bookings</h3>
          <p className="text-slate-500 text-xs mt-1">
            Enter your ad spend per campaign to see return on ad spend (ROAS) and net profit. Revenue is paid bookings
            attributed to each campaign in this window.
          </p>
        </div>
        {data.campaigns.length === 0 ? (
          <p className="text-slate-500 text-sm px-6 pb-6">No traffic recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-y border-slate-800">
                  <th className="text-left font-normal px-6 py-3">Source</th>
                  <th className="text-left font-normal px-4 py-3">Medium</th>
                  <th className="text-left font-normal px-4 py-3">Campaign</th>
                  <th className="text-right font-normal px-4 py-3">Visitors</th>
                  <th className="text-right font-normal px-4 py-3">Paid</th>
                  <th className="text-right font-normal px-4 py-3">Spend €</th>
                  <th className="text-right font-normal px-4 py-3">Revenue €</th>
                  <th className="text-right font-normal px-4 py-3">ROAS</th>
                  <th className="text-right font-normal px-6 py-3">Net €</th>
                </tr>
              </thead>
              <tbody>
                {data.campaigns.map((c, i) => {
                  const rowKey = `${c.source}-${c.medium}-${c.campaign}-${i}`;
                  const paid = (c.paid_bookings ?? c.bookings_paid) + (c.subscriptions || 0);
                  return (
                    <tr key={rowKey} className="border-b border-slate-900" data-testid={`campaign-row-${i}`}>
                      <td className="px-6 py-3 text-white">{c.source}</td>
                      <td className="px-4 py-3 text-slate-400">{c.medium}</td>
                      <td className="px-4 py-3 text-slate-400 font-mono text-xs">{c.campaign}</td>
                      <td className="px-4 py-3 text-right font-mono text-slate-300">{c.visitors}</td>
                      <td className="px-4 py-3 text-right font-mono text-[#D4AF37]">{paid}</td>
                      <td className="px-4 py-3 text-right">
                        <input
                          key={`spend-${rowKey}-${c.spend_eur}`}
                          type="number"
                          min="0"
                          step="10"
                          defaultValue={c.spend_eur ? c.spend_eur : ""}
                          placeholder="0"
                          data-testid={`campaign-spend-input-${i}`}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.target.blur();
                          }}
                          onBlur={(e) => saveSpend(c, e.target.value)}
                          className="w-20 bg-slate-950 border border-slate-800 focus:border-[#D4AF37] outline-none text-right text-slate-200 font-mono text-xs px-2 py-1"
                        />
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-slate-300">
                        {c.revenue_eur ? `€${Number(c.revenue_eur).toLocaleString()}` : "—"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {c.roas != null ? (
                          <span className={c.roas >= 1 ? "text-green-400" : "text-red-400"}>{c.roas}×</span>
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-right font-mono">
                        {c.net_eur != null ? (
                          <span className={c.net_eur >= 0 ? "text-green-400" : "text-red-400"}>
                            {c.net_eur < 0 ? "−" : ""}€{Math.abs(c.net_eur).toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="border border-slate-800 bg-slate-900/50" data-testid="marketing-variants">
        <h3 className="font-serif text-lg text-white p-6 pb-4">Landing variants</h3>
        {data.variants?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-y border-slate-800">
                  <th className="text-left font-normal px-6 py-3">Variant</th>
                  <th className="text-right font-normal px-4 py-3">Visitors</th>
                  <th className="text-right font-normal px-4 py-3">Quiz done</th>
                  <th className="text-right font-normal px-4 py-3">Emails</th>
                  <th className="text-right font-normal px-4 py-3">Signups</th>
                  <th className="text-right font-normal px-6 py-3">Paid</th>
                </tr>
              </thead>
              <tbody>
                {data.variants.map((v) => (
                  <tr key={v.variant} className="border-b border-slate-900" data-testid={`variant-row-${v.variant}`}>
                    <td className="px-6 py-3 text-white">
                      {v.variant === "default" ? "default (no ?v=)" : `?v=${v.variant}`}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-300">{v.visitors}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-300">{v.quiz_completed}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-300">{v.emails_captured}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-300">{v.signups}</td>
                    <td className="px-6 py-3 text-right font-mono text-[#D4AF37]">{v.paid}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-slate-500 text-sm px-6 pb-6">No variant traffic yet.</p>
        )}
      </div>

      <AttributionPanel />

      <DigestSettingsPanel />

      <div className="border border-slate-800 bg-slate-900/50" data-testid="marketing-leads">
        <h3 className="font-serif text-lg text-white p-6 pb-4">Leads and where they came from</h3>
        {data.leads.length === 0 ? (
          <p className="text-slate-500 text-sm px-6 pb-6">No leads in this window yet.</p>
        ) : (
          <div className="divide-y divide-slate-900">
            {data.leads.map((lead, i) => (
              <div key={`${lead.email}-${i}`} className="px-6 py-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-white text-sm truncate">
                    {lead.name || "Unknown"} <span className="text-slate-500">· {lead.email}</span>
                  </p>
                  <p className="text-slate-500 text-xs mt-1">
                    {lead.type} · {lead.detail} · {fmtDate(lead.created_at)}
                  </p>
                </div>
                <p className="text-xs font-mono text-slate-400">
                  {lead.source}
                  {lead.campaign !== "—" && <span className="text-[#D4AF37]"> / {lead.campaign}</span>}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMarketing;
